import { DepartmentResource } from '../../../entities/Departments/Departments'
import { translate } from '../../../helpers/translations/translator'
import { Role, User } from '../../../models'
import { setPagination } from '../../../redux/pagination/paginationActions'
import { FileFormData, GetFilesParams } from '../../entities/File'
import {
    deleteFileApiCall,
    getFilesAsDocumentsApiCall,
    getFilesForUserApiCall,
    getFilesMetaApiCall,
    patchDocumentApiCall,
    patchUserDocumentApiCall,
    postDocumentApiCall,
    postUserDocumentApiCall,
} from '../../entities/File'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import trans from '../../helpers/trans'
import { ToastType } from '../../types'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import {
    documentAdded,
    documentDeleted,
    documentPatched,
    removeLoadingFile,
    setAmountOfPages,
    setDocuments,
    setError,
    setIsLoading,
    setIsSuccessful,
    setLoadingFile,
    setUserDocuments,
    userDocumentAdded,
    userDocumentDeleted,
    userDocumentPatched,
} from './filesReducer'

// =============================================================================
// PZ Documents (company-wide documents)
// =============================================================================

/**
 * Fetch documents data and meta (for initial load and filter changes)
 */
export const getDocuments = (params: GetFilesParams) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const [dataResponse, metaResponse] = await Promise.all([
            getFilesAsDocumentsApiCall(params),
            getFilesMetaApiCall(params),
        ])

        if (!isFetchResultSuccessful(dataResponse)) {
            dispatch(setError(dataResponse.error))
            return
        }

        dispatch(setDocuments(dataResponse.data))

        if (isFetchResultSuccessful(metaResponse)) {
            const { total } = metaResponse.data
            const requestedItems = params.items || 10
            const amountOfPages = requestedItems > 0 ? Math.ceil(total / requestedItems) : 1
            dispatch(setAmountOfPages(amountOfPages))
            dispatch(setPagination(amountOfPages))
        }
    } catch (error) {
        console.error('[getDocuments]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsLoading(false))
    }
}

/**
 * Fetch documents data only (for page changes - no meta fetch needed)
 */
export const getDocumentsPage = (params: GetFilesParams) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const dataResponse = await getFilesAsDocumentsApiCall(params)

        if (!isFetchResultSuccessful(dataResponse)) {
            dispatch(setError(dataResponse.error))
            return
        }

        dispatch(setDocuments(dataResponse.data))
    } catch (error) {
        console.error('[getDocumentsPage]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const addDocument = (data: FileFormData) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))
    dispatch(setIsSuccessful(false))

    try {
        const response = await postDocumentApiCall(data)

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return
        }

        dispatch(documentAdded(response.data))
        dispatch(setIsSuccessful(true))
        dispatch(setToast({
            text: translate('pages.management.addDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editDocument = (id: string, data: FileFormData) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setLoadingFile(id))
    dispatch(setError(''))
    dispatch(setIsSuccessful(false))

    try {
        const response = await patchDocumentApiCall(id, data)

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return
        }

        dispatch(documentPatched(response.data))
        dispatch(setIsSuccessful(true))
        dispatch(setToast({
            text: translate('pages.management.editDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[editDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(removeLoadingFile(id))
    }
}

export const removeDocument = (id: string) => async (dispatch: TypedDispatch): Promise<boolean> => {
    dispatch(setLoadingFile(id))
    dispatch(setError(''))

    try {
        const response = await deleteFileApiCall(id)

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return false
        }

        dispatch(documentDeleted(id))
        dispatch(setToast({
            text: translate('pages.management.deleteDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

        return true
    } catch (error) {
        console.error('[removeDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
        return false
    } finally {
        dispatch(removeLoadingFile(id))
    }
}

// =============================================================================
// User Documents (management page - user-specific documents)
// =============================================================================

export const getUserDocuments = (user: User) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await getFilesForUserApiCall(
            user.id,
            user.departments,
            user.roles,
        )

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return
        }

        dispatch(setUserDocuments(response.data))
    } catch (error) {
        console.error('[getUserDocuments]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const addUserDocument = (
    data: FileFormData,
    hrUserIds: string[],
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))
    dispatch(setIsSuccessful(false))

    try {
        const response = await postUserDocumentApiCall(
            { ...data, hr: hrUserIds },
            userId,
            userDepartments,
            userRoles,
        )

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return
        }

        dispatch(userDocumentAdded(response.data))
        dispatch(setIsSuccessful(true))
        dispatch(setToast({
            text: translate('pages.management.addDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addUserDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editUserDocument = (
    id: string,
    data: FileFormData,
    hrUserIds: string[],
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setLoadingFile(id))
    dispatch(setError(''))
    dispatch(setIsSuccessful(false))

    try {
        const response = await patchUserDocumentApiCall(
            id,
            { ...data, hr: hrUserIds },
            userId,
            userDepartments,
            userRoles,
        )

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return
        }

        dispatch(userDocumentPatched(response.data))
        dispatch(setIsSuccessful(true))
        dispatch(setToast({
            text: translate('pages.management.editDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[editUserDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        dispatch(removeLoadingFile(id))
    }
}

export const removeUserDocument = (id: string) => async (dispatch: TypedDispatch): Promise<boolean> => {
    dispatch(setLoadingFile(id))
    dispatch(setError(''))

    try {
        const response = await deleteFileApiCall(id)

        if (!isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            return false
        }

        dispatch(userDocumentDeleted(id))
        dispatch(setToast({
            text: translate('pages.management.deleteDocumentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

        return true
    } catch (error) {
        console.error('[removeUserDocument]', error)
        dispatch(setError(trans('errors.unknownError')))
        return false
    } finally {
        dispatch(removeLoadingFile(id))
    }
}
